import React, {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { AppError } from "../../../../../errors/errorProcessors"
import usePublishAmendment from "../../../../../services/middleware/hooks/devices/ttr/usePublishAmendment"
import useSaveDeviceDraft from "../../../../../services/middleware/hooks/devices/ttr/useSaveDeviceDraft"
import { TTypeTestVersionPostBody } from "../../../../../services/middleware/types/devices/ttr/publishDeviceTypes"
import { checkContextErrors } from "../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { TTRDeviceFormData } from "../../types/providerTypes"

type AmendDeviceProviderProps = {
    formData: TTRDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<TTRDeviceFormData>>
    formDataIsLoading: boolean
    formDataError?: AppError
    deviceId?: string
    deviceRef?: string
    attachmentUUID?: string
    supersededDeviceID?: number
    complianceComments?: string
    complianceStatusNotes?: string
    typeTestVersionPostBody?: TTypeTestVersionPostBody
    children: ReactNode
}

export const AmendDeviceContext = createContext(
    {} as IDeviceFormContextValue<TTRDeviceFormData>
)

const AmendDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    formDataError,
    deviceId,
    deviceRef,
    attachmentUUID,
    supersededDeviceID,
    complianceComments,
    complianceStatusNotes,
    typeTestVersionPostBody,
    children,
}: AmendDeviceProviderProps): ReactElement => {
    const [newDeviceId, setNewDeviceId] = useState<string | undefined>()
    const [newDeviceRef, setNewDeviceRef] = useState<string | undefined>()
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishAmendment,
        isLoading: publishInProgress,
        isLoaded: publishSuccessful,
        error: publishError,
    } = usePublishAmendment(formData, attachmentUUID, deviceId, deviceRef)

    const onSubmit = useCallback(async (): Promise<void> => {
        const res = await publishAmendment()
        setNewDeviceId(res.published_device_id)
        setNewDeviceRef(res.amendment_ref)
    }, [publishAmendment])

    const {
        saveDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveDeviceDraft(
        deviceRef,
        deviceId,
        attachmentUUID,
        supersededDeviceID,
        complianceComments,
        complianceStatusNotes,
        typeTestVersionPostBody
    )

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const value: IDeviceFormContextValue<TTRDeviceFormData> = useMemo(
        () => ({
            attachmentUUID,
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            deviceId: newDeviceId ?? deviceId,
            deviceRef: newDeviceRef ?? deviceRef,
            onSubmit,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer: false,
            formIsLoading: formDataIsLoading,
            saveDraft: saveDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        }),
        [
            attachmentUUID,
            formData,
            setFormData,
            currentIndex,
            newDeviceId,
            deviceId,
            newDeviceRef,
            deviceRef,
            onSubmit,
            publishInProgress,
            publishSuccessful,
            formDataIsLoading,
            saveDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        ]
    )

    return (
        <AmendDeviceContext.Provider value={value}>
            {children}
        </AmendDeviceContext.Provider>
    )
}

AmendDeviceProvider.defaultProps = {
    formDataError: undefined,
    deviceId: undefined,
    deviceRef: undefined,
    attachmentUUID: undefined,
    supersededDeviceID: undefined,
    complianceComments: undefined,
    complianceStatusNotes: undefined,
    typeTestVersionPostBody: undefined,
}

export default AmendDeviceProvider
